import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getDatabase, ref, get } from 'firebase/database';
import firebaseApp from './adm/admin/firebaseConfig';
import SidebarNavigation from './utils/SideBarNavigation';
import {
  Container, Box, Typography, CardMedia, Grid, CircularProgress,
  Paper, IconButton, Divider, Stack, Chip, Avatar, Tooltip
} from "@mui/material";
import {
  Download as DownloadIcon,
  CalendarMonth,
  Person,
  Label,
  Image as ImageIcon,
  ArrowBack
} from '@mui/icons-material';
import TitleSection from './utils/TittleTheme';
import RandomNoticias from './utils/RamdomNoticias';
import { Helmet, HelmetProvider } from 'react-helmet-async';

const db = getDatabase(firebaseApp);

export default function ViewNoticia() {
  const [noticias, setNoticias] = useState({});
  const [formattedDate, setFormattedDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const { id } = useParams();

  const fetchNoticia = async () => {
    const noticiasRef = ref(db, `noticias/${id}`);
    try {
      const snapshot = await get(noticiasRef);
      if (snapshot.exists()) {
        const noticiaData = snapshot.val();
        const parsedDate = new Date(noticiaData.data);
        if (!isNaN(parsedDate)) {
          setFormattedDate(format(parsedDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR }));
        }
        setNoticias(noticiaData);
        setSelectedImage(noticiaData.imagem); // Inicia com a capa em destaque
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNoticia();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f5f5f5' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={60} thickness={4} sx={{ color: '#0F71FD' }} />
          <Typography variant="overline">Carregando notícia...</Typography>
        </Stack>
      </Box>
    );
  }

  // Lista de imagens da galeria (capa + adicionais)
  const galeria = [noticias.imagem, noticias.imagem1, noticias.imagem2, noticias.imagem3, noticias.imagem4, noticias.imagem5, noticias.imagem6, noticias.imagem7, noticias.imagem8].filter(Boolean);

  return (
    <HelmetProvider>
      <Helmet>
        <title>{noticias.titulo}</title>
        <meta name="description" content={noticias.titulo} />
      </Helmet>

      <Box sx={{ bgcolor: '#F8F9FA', minHeight: '100vh', pb: 8 }}>
        <Container maxWidth="lg" sx={{ pt: 4 }}>
          <Grid container spacing={4}>

            {/* COLUNA PRINCIPAL */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 4, border: '1px solid #E0E0E0' }}>

                {/* Meta Info */}
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label={noticias.categoria || 'Geral'}
                    size="small"
                    sx={{ bgcolor: '#0F71FD', color: '#FFF', fontWeight: 'bold', textTransform: 'uppercase' }}
                  />
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: '#777' }}>
                    <CalendarMonth sx={{ fontSize: 16 }} />
                    <Typography variant="caption">{formattedDate}</Typography>
                  </Stack>
                </Stack>

                <Typography variant="h3" component="h1" sx={{
                  // fontWeight: 800, 
                  color: '#1A1A1A',
                  mb: 3,
                  // lineHeight: 1.2,
                  fontSize: { xs: '1.8rem', md: '2.5rem' }
                }}>
                  {noticias.titulo}
                </Typography>



                {/* Imagem Principal / Destaque da Galeria */}
                <Box sx={{ position: 'relative', mb: 4, borderRadius: 3, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                  <CardMedia
                    component="img"
                    image={selectedImage}
                    alt={noticias.titulo}
                    sx={{ width: '100%', maxHeight: 500, objectFit: 'cover', transition: '0.5s ease' }}
                  />
                </Box>

                {/* Galeria de Miniaturas */}
                {galeria.length > 1 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 2, fontWeight: 'bold' }}>
                      <ImageIcon sx={{ mr: 1, color: '#0F71FD' }} /> GALERIA DE FOTOS
                    </Typography>
                    <Grid container spacing={1}>
                      {galeria.map((img, index) => (
                        <Grid item xs={3} sm={2} key={index}>
                          <Box
                            onClick={() => setSelectedImage(img)}
                            sx={{
                              height: 70,
                              borderRadius: 2,
                              cursor: 'pointer',
                              overflow: 'hidden',
                              border: selectedImage === img ? '3px solid #0F71FD' : '2px solid transparent',
                              opacity: selectedImage === img ? 1 : 0.7,
                              '&:hover': { opacity: 1 }
                            }}
                          >
                            <img src={img} alt="Miniatura" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}


                <Divider sx={{ mb: 4 }} />

                {/* Conteúdo da Notícia (HTML do ReactQuill) */}
                <Box
                  className="news-content"
                  sx={{
                    '& p': { mb: 2, lineHeight: 1.8, color: '#333', fontSize: '1.1rem' },
                    '& img': { maxWidth: '100%', borderRadius: 2 },
                    '& h2, & h3': { color: '#1A1A1A', mt: 4, mb: 2 },
                    textAlign: 'justify'
                  }}
                  dangerouslySetInnerHTML={{ __html: noticias.descricao }}
                />
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, mt: 3, p: 2, bgcolor: '#F1F1F1', borderRadius: 2 }}>
                  <Avatar sx={{ bgcolor: '#0F71FD' }}><Person /></Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ lineHeight: 1 }}>Por: {noticias.autor}</Typography>
                    <Typography variant="caption" color="textSecondary">Fonte: {noticias.fonte}</Typography>
                  </Box>
                </Stack>
                {/* Tags */}
                {noticias.tags && noticias.tags.length > 0 && (
                  <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid #EEE' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Label sx={{ color: '#0F71FD', transform: 'rotate(-45deg)' }} />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {noticias.tags.map((tag, idx) => (
                          <Chip key={idx} label={`#${tag}`} variant="outlined" size="small" onClick={() => { }} sx={{ cursor: 'pointer' }} />
                        ))}
                      </Box>
                    </Stack>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* SIDEBAR */}
            <Grid item xs={12} md={4}>
              <Box sx={{ position: 'sticky', top: 20 }}>
                <SidebarNavigation noticia={true} />
              </Box>
            </Grid>

            {/* RELACIONADAS */}
            <Grid item xs={12}>
              <Box sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" sx={{ fontWeight: 900, color: '#1A1A1A' }}>
                  NOTÍCIAS RELACIONADAS
                </Typography>
                <Divider sx={{ flexGrow: 1, ml: 3, bgcolor: '#0F71FD', height: 2 }} />
              </Box>
              <RandomNoticias quantidade={3} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </HelmetProvider>
  );
}